import React, { useCallback, useEffect, useRef } from "react";
import { IContextualMenuProps } from "@fluentui/react";
import { formatMessageDefault } from "../../../utils/helpers/LanguageHelper";
import { UseOutlineSearchResult } from "./useOutlineSearch";
import { DocumentPermissionTO } from "../../../api/models/document-permission-to";
import { SkeletonLoader } from "../../../common/loading/SkeletonLoader";
import { OutlineSearchResultRow } from "./OutlineSearchResultRow";
import { selectContentControlById } from "../../../office/ContentControl";
import { useAppSelector } from "../../../redux/hooks/hooks";
import { hasActiveSearchCriteria, selectOutlineFilter } from "../../../redux/feature/cpm/outline-search-slice";
import "./outline-search.css";

export interface OutlineSearchResultsProps {
  search: UseOutlineSearchResult;
  /** The root id of the currently opened document (for single-click CC highlight) */
  openedDocRootId: string | null;
  onOpenDocPart: (docPartId: string) => void;
  /** Factory that returns the contextual-menu props for a given search row */
  getWorkflowStatusMenuProps?: (row: DocumentPermissionTO) => IContextualMenuProps;
  /** The scroll container that hosts this list; passed as IntersectionObserver root for infinite scroll */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}

/** Renders outline search results: skeleton, error, empty state, result rows + infinite scroll */
export const OutlineSearchResults: React.FC<OutlineSearchResultsProps> = ({
  search,
  openedDocRootId,
  onOpenDocPart,
  getWorkflowStatusMenuProps,
  scrollContainerRef,
}) => {
  const {
    query,
    results,
    status,
    nextPageStatus,
    hasMore,
    error,
    scopeRootId,
    selectedDocPartId,
    fetchNextPage,
    selectResult,
    refireQuery,
  } = search;

  const sentinelRef = useRef<HTMLLIElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // IDS-5755 (FE-7): filter state lives in the outline-search slice, not on the `search` hook result.
  const { assignedToMe, statusFilter } = useAppSelector(selectOutlineFilter);

  // Set up IntersectionObserver on the sentinel
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    if (!sentinelRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && nextPageStatus !== "loading") {
          fetchNextPage();
        }
      },
      { root: scrollContainerRef?.current ?? null, rootMargin: "200px 0px", threshold: 0 }
    );
    observerRef.current.observe(sentinelRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [hasMore, nextPageStatus, fetchNextPage, scrollContainerRef]);

  const handleSingleClick = useCallback(
    (row: DocumentPermissionTO) => {
      // Select the content control in Word only when scope matches opened doc
      if (scopeRootId && scopeRootId === openedDocRootId) {
        const ccId = row.documentPart.contentControlId;
        if (ccId != null) {
          selectContentControlById(ccId).catch(() => undefined);
        }
      }
      selectResult(row);
    },
    [scopeRootId, openedDocRootId, selectResult]
  );

  const handleDoubleClick = useCallback(
    (row: DocumentPermissionTO) => {
      onOpenDocPart(row.documentPart.id);
    },
    [onOpenDocPart]
  );

  // Empty-scope hint: an active search (text >= 3 chars, assignedToMe, or a status filter), no scope root, idle
  if (hasActiveSearchCriteria(query, assignedToMe, statusFilter) && !scopeRootId && status === "idle") {
    return (
      <div className="outline-search-results">
        <p className="outline-search-hint">Open or expand a document to search within it.</p>
      </div>
    );
  }

  // Loading first page
  if (status === "loading") {
    return (
      <div className="outline-search-results">
        <SkeletonLoader />
      </div>
    );
  }

  // First-page error
  if (status === "error") {
    return (
      <div className="outline-search-results">
        <p className="outline-search-error">
          {formatMessageDefault("cof.outline.search.error", "Search failed.")}
          {error && <span className="outline-search-error-detail"> {error}</span>}
          <button className="outline-search-error-retry" onClick={refireQuery}>
            {formatMessageDefault("cof.outline.search.retry", "Retry")}
          </button>
        </p>
      </div>
    );
  }

  // Success but empty
  if (status === "success" && results.length === 0) {
    return (
      <div className="outline-search-results">
        <p className="outline-search-empty">
          {formatMessageDefault("cof.outline.search.no.results", "No document parts match your search.")}
        </p>
      </div>
    );
  }

  // Success with results
  if (status === "success" && results.length > 0) {
    return (
      <ul className="outline-search-results">
        {results.map((row) => (
          <li key={row.documentPart.id} className="outline-search-result-item">
            <OutlineSearchResultRow
              row={row}
              isSelected={selectedDocPartId === row.documentPart.id}
              onSingleClick={handleSingleClick}
              onDoubleClick={handleDoubleClick}
              getWorkflowStatusMenuProps={getWorkflowStatusMenuProps}
            />
          </li>
        ))}

        {/* Infinite scroll sentinel */}
        <li ref={sentinelRef} className="outline-search-sentinel" aria-hidden="true" />

        {nextPageStatus === "loading" && (
          <li className="outline-search-next-spinner" aria-hidden="true">
            <SkeletonLoader />
          </li>
        )}

        {nextPageStatus === "error" && (
          <li className="outline-search-next-error">
            {formatMessageDefault("cof.outline.search.next.error", "Failed to load more.")}
            <button className="outline-search-next-retry" onClick={fetchNextPage}>
              {formatMessageDefault("cof.outline.search.retry", "Retry")}
            </button>
          </li>
        )}
      </ul>
    );
  }

  return null;
};
